import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, isRef as _isRef, createVNode as _createVNode, withCtx as _withCtx, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, createTextVNode as _createTextVNode, createCommentVNode as _createCommentVNode, createBlock as _createBlock, resolveComponent as _resolveComponent } from "vue"

const _hoisted_1 = { class: "permissions-wrapper" }
const _hoisted_2 = { class: "institute-selet row gap-06 w-full" }
const _hoisted_3 = { class: "font-m" }
const _hoisted_4 = { class: "margin-h3" }
const _hoisted_5 = { class: "dialog-header w-full pright-10" }
const _hoisted_6 = {
  key: 0,
  class: "flex-col h-full"
}
const _hoisted_7 = { class: "modal-content-scroll accordion" }
const _hoisted_8 = {
  key: 0,
  class: "row-space-between w-full permission-block bg-clearblue"
}
const _hoisted_9 = { key: 1 }
const _hoisted_10 = { class: "modal-fixed-bottom" }

import { useI18n } from "vue-i18n";
import Accordion from 'primevue/accordion';
import AccordionPanel from 'primevue/accordionpanel';
import AccordionHeader from 'primevue/accordionheader';
import AccordionContent from 'primevue/accordioncontent';
import { GroupedPermissions, PermissionCategory, Permissions, PermissionsArray, } from "@/models/permissions.model";
import { ref, computed, watch, onMounted } from "vue";
import { ApiError, ItemSelect } from "@/models/share.model";
import DataTable from 'primevue/datatable';
import Column from 'primevue/column';
import { AdminApi, AdminUpdatePermissionRequest, InstituteApi, Permission, UpdatePermissionRequest, UsersPermissions } from "@/apiGenerate/medserver";
import { InstituteLightResponse } from "@/apiGenerate/medserver/models/InstituteLightResponse";
import { medServerApiConfig } from "@/config/medserver-api.config";
import { useToast } from "primevue";
import { toastError, toastSuccess } from '@/utils/toast-utils';
import ToggleSwitch from 'primevue/toggleswitch';
import Dialog from 'primevue/dialog';
import Select from 'primevue/select';
import { useAppUserStore } from "@/stores/appUser";


export default /*@__PURE__*/_defineComponent({
  __name: 'esPermissionSetting',
  props: {
  adminEsk: {
    type: Boolean,
    default: false
  },
  instituteList: {
    type: Array<InstituteLightResponse>,
    default: []
  }
},
  emits: ['refresh-health-pro'],
  setup(__props, { emit: __emit }) {

const props = __props

const { t } = useI18n();
const appUserStore = useAppUserStore();
const refreshData = ref<boolean>(false);
const selectedDoctor = ref(null as null | typeof doctorList.value[0]);
const selectedPermissions = ref<{ [code: string]: boolean }>({});
const activeIndex = ref<number[]>([]);
const doctorList = ref<UsersPermissions[]>([]);
const allExpanded = ref<boolean>(true);
const showPermissionsModal = ref<boolean>(false);

let selectedInstitute = ref<ItemSelect>(null);
const instituteApi = new InstituteApi(medServerApiConfig);
const adminApi = new AdminApi(medServerApiConfig);
const toast = useToast();

const emits = __emit;

const transformToItemSelect = (dataToTransform: any): any => {
    return {
        id: dataToTransform.instituteWithRoleId,
        name: dataToTransform.instituteName
    }
};

const transformInstituteLightResponseToItemSelect = (dataToTransform: InstituteLightResponse): any => {
    return {
        id: dataToTransform.id,
        name: dataToTransform.name
    }
};

/**
 * Computes grouped permissions from the Permissions object.
 * Filters out flat (non-object) permissions.
 *
 * @returns A list of permission groups with `category` and `perms`
 */
const groupedPermissions = computed<GroupedPermissions[]>(() => {
  return Object.entries(Permissions)
    .map(([category, perms]) => ({
      category: category as PermissionCategory,
      perms: Object.values(perms).filter(p => {
        if (props.adminEsk) {
          return p.settingsEsk;
        } else {
          return p.settingsInstitue && !p.settingsEsk
        }
      }
      ),
    }))
    .filter(group => group.perms.length > 0);
});


const healthGroupMemberships = computed(() => {

  if (props.instituteList.length > 0) {
    return props.instituteList
            .map(institute => transformInstituteLightResponseToItemSelect(institute));
  }

  return appUserStore.currentAppUserData.healthGroupMemberships
            .map(healthGroup => transformToItemSelect(healthGroup.instituteWithRole));
});

const currInstitute = computed((): ItemSelect => {
  if (!appUserStore.currentHealthGroupMembership) {return null;}
  const currentIwr = appUserStore.currentHealthGroupMembership.instituteWithRole;
  return transformToItemSelect(currentIwr);
});

/**
 * Initializes the selected permissions for a given user.
 * Converts the user's permission list into a boolean map by permission code.
 *
 * @param permissions - The list of permissions for the selected user
 */
const initUserPermissions = (permissions: Permission[]) => {
  selectedPermissions.value = {};

  const permissionStateMap = new Map(permissions.map(p => [p.name, (p as any).active ?? false]));

  for (const perm of PermissionsArray) {
    selectedPermissions.value[perm.name] = permissionStateMap.get(perm.name) ?? false;
  }
};

const hasAccessPlanning = () => {
  return selectedPermissions.value[Permissions.planning.ACCESS_PLANNING.name];
}
/**
 * Returns the i18n translation key for a given permission.
 *
 * @param category - The permission category (e.g. "ADMIN_INSTITUTE", "PATIENT", etc.)
 * @param code - The permission code
 * @returns The corresponding translation key
 */
const getTranslationKey = (category: string, code: string): string => {
  return `settings.permission.roles.${category}.${code}`;
};

/**
 * Selects or deselects a doctor.
 * Initializes their permissions when selected.
 *
 * @param doctor - The doctor to select or deselect
 */
const selectDoctor = (doctor: typeof doctorList.value[0]) => {
  if (selectedDoctor.value?.id === doctor.id) {
    selectedDoctor.value = null;
    selectedPermissions.value = {};
    activeIndex.value = [];
    allExpanded.value = false;
  } else {
    selectedDoctor.value = doctor;
    initUserPermissions(doctor.permissions);
    openAllTabs();
    allExpanded.value = true;
  }
}


/**
 * Expands all permission groups (accordion panels).
 */
const openAllTabs = () => {
  const totalTabs = groupedPermissions.value.length;
  activeIndex.value = Array.from({ length: totalTabs }, (_, i) => i);
}

/**
 * Sets the selected institute and triggers user permissions reload.
 *
 * @param institute - The selected institute
 */
const selectInstitute = (institute: ItemSelect) => {
  selectedInstitute.value = institute;
  refreshData.value = true;
}

/**
 * Returns a custom CSS class for a table row.
 * Highlights the row if the doctor is currently selected.
 *
 * @param rowData - The data of the table row
 * @returns A CSS class name if selected, otherwise an empty string
 */
const getRowClass = (rowData: any) => {
  return selectedDoctor.value && selectedDoctor.value.id === rowData.id
    ? 'selected-row'
    : '';
};

/**
 * Fetches all users' permissions for the currently selected institute.
 * Updates the doctor list (`doctorList`) accordingly.
 */
const getUsersPermission = async () =>{
  await instituteApi.getPermissionsByUsers({ instituteId: selectedInstitute.value.id, personId: selectedInstitute.value.id }).then((response: UsersPermissions[]) => {
    doctorList.value = response
  });
}

/**
 * Handles permission checkbox toggle for the selected doctor.
 *
 * @param code - The permission code being toggled
 * @param value - The new boolean value of the checkbox
 */
const handlePermissionToggle = async (user: UsersPermissions, code: string, value: boolean) => {
  if (!selectedDoctor.value) return;

  let isUpdate: boolean
  const permission = PermissionsArray.find(perm => perm.name == code)
  try {
    if (permission.settingsInstitue) {
      const updatePayload: UpdatePermissionRequest = {
        instituteId: selectedInstitute.value.id,
        personId: user.id,
        updateUsersPermissionsRequest : {
          permissionName: code,
          active: value,
          }
      };
      isUpdate = await instituteApi.updatePermission(updatePayload);
    } else {
      const request : AdminUpdatePermissionRequest = {
        personId : user.id,
        updateUsersPermissionsRequest : {
          permissionName: code,
          active: value,
          instituteId: selectedInstitute.value.id
        }
      }
      isUpdate = await adminApi.adminUpdatePermission(request);
    }

    if (isUpdate) {
      selectedPermissions.value[code] = value;
      toastSuccess(toast, {
        summary: t('common.toast.success.update'),
        detail:  t('settings.params.permission.update')
      });
      await getUsersPermission();
      if (Permissions.planning.OWNER_PLANNING.name === code) {
        emits('refresh-health-pro', value);
      }
    } 
  } catch (error: unknown) {
    const err = error as ApiError;
    selectedPermissions.value[code] = !value;
    toastError(toast, {
      summary: t('common.toast.error'),
      detail: t(`error.${err.status}`)
    });
  }
}

/**
 * Open the modal
 */
const openPermissionsModal = (event: any) => {
  const doctor = event.data;
  selectDoctor(doctor);
  showPermissionsModal.value = true;
}

/**
 * Close the modal and unselect the doctor.
 */
const closePermissionsModal = () => {
  selectedDoctor.value = null;
  selectedPermissions.value = {};
  activeIndex.value = [];
  allExpanded.value = false;
  showPermissionsModal.value = false;
}

/**
 * Watches the selected institute and reloads user permissions when it changes.
 */
watch(selectedInstitute, async (newInstitute) => {
  if (newInstitute?.id) {
    selectedDoctor.value = null;
    await getUsersPermission();
  }
});

watch(
  () => currInstitute.value,
  (newCurrInstitute: ItemSelect) => {
    if (newCurrInstitute) {
        selectInstitute(newCurrInstitute);
    }
  },
  { deep: true }
);

onMounted(() => {
  if(!props.adminEsk)
    selectedInstitute.value = currInstitute.value;
})

return (_ctx: any,_cache: any) => {
  const _component_es_button = _resolveComponent("es-button")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      _createElementVNode("span", _hoisted_3, _toDisplayString(_unref(t)('settings.permissions.institutes')), 1),
      _createVNode(_unref(Select), {
        modelValue: _unref(selectedInstitute),
        "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => (_isRef(selectedInstitute) ? (selectedInstitute).value = $event : selectedInstitute = $event)),
        options: healthGroupMemberships.value,
        onValueChange: _cache[1] || (_cache[1] = ($event: any) => (selectInstitute($event))),
        size: "small",
        class: "w-full",
        optionLabel: "name",
        filter: ""
      }, null, 8, ["modelValue", "options"])
    ]),
    _createElementVNode("h3", _hoisted_4, _toDisplayString(_unref(t)('settings.permission.datatableTitle')), 1),
    _createVNode(_unref(DataTable), {
      value: doctorList.value,
      dataKey: "id",
      onRowClick: openPermissionsModal,
      selectionMode: "single",
      selection: selectedDoctor.value,
      class: "w-full",
      stripedRows: "",
      rowClass: getRowClass,
      tableClass: "w-full"
    }, {
      default: _withCtx(() => [
        _createVNode(_unref(Column), {
          field: "firstname",
          header: _unref(t)('patient.entourageCard.firstName')
        }, null, 8, ["header"]),
        _createVNode(_unref(Column), {
          field: "lastname",
          header: _unref(t)('patient.entourageCard.name')
        }, null, 8, ["header"])
      ]),
      _: 1
    }, 8, ["value", "selection"]),
    _createVNode(_unref(Dialog), {
      visible: showPermissionsModal.value,
      "onUpdate:visible": _cache[2] || (_cache[2] = ($event: any) => ((showPermissionsModal).value = $event)),
      modal: true,
      closable: false,
      class: "flex-col",
      style: { width: '45vw', height: '60vh' },
      draggable: false,
      dismissableMask: true,
      onHide: closePermissionsModal
    }, {
      header: _withCtx(() => [
        _createElementVNode("div", _hoisted_5, [
          _createElementVNode("span", null, _toDisplayString(_unref(t)('settings.permission.title', { name: `${selectedDoctor.value.title ? selectedDoctor.value.title + ' ' + selectedDoctor.value.lastname : selectedDoctor.value.lastname}` })), 1)
        ])
      ]),
      default: _withCtx(() => [
        (selectedDoctor.value)
          ? (_openBlock(), _createElementBlock("div", _hoisted_6, [
              _createElementVNode("div", _hoisted_7, [
                _createVNode(_unref(Accordion), {
                  multiple: true,
                  value: activeIndex.value
                }, {
                  default: _withCtx(() => [
                    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(groupedPermissions.value, ({ category, perms }, index) => {
                      return (_openBlock(), _createBlock(_unref(AccordionPanel), {
                        key: category,
                        value: index
                      }, {
                        default: _withCtx(() => [
                          _createVNode(_unref(AccordionHeader), null, {
                            default: _withCtx(() => [
                              _createTextVNode(_toDisplayString(_unref(t)(`settings.permission.roles.${category}.label`)), 1)
                            ]),
                            _: 2
                          }, 1024),
                          _createVNode(_unref(AccordionContent), null, {
                            default: _withCtx(() => [
                              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(perms, (code) => {
                                return (_openBlock(), _createElementBlock("div", {
                                  key: code.name
                                }, [
                                  (code.name !== _unref(Permissions).planning.OWNER_PLANNING.name || hasAccessPlanning())
                                    ? (_openBlock(), _createElementBlock("div", _hoisted_8, [
                                        _createElementVNode("span", null, _toDisplayString(_unref(t)(getTranslationKey(category, code.name))), 1),
                                        _createVNode(_unref(ToggleSwitch), {
                                          modelValue: selectedPermissions.value[code.name],
                                          "onUpdate:modelValue": ($event: any) => ((selectedPermissions.value[code.name]) = $event),
                                          onChange: () => handlePermissionToggle(selectedDoctor.value, code.name, selectedPermissions.value[code.name])
                                        }, null, 8, ["modelValue", "onUpdate:modelValue", "onChange"])
                                      ]))
                                    : (_openBlock(), _createElementBlock("span", _hoisted_9, _toDisplayString(_unref(t)('settings.permission.empty')), 1))
                                ]))
                              }), 128))
                            ]),
                            _: 2
                          }, 1024)
                        ]),
                        _: 2
                      }, 1032, ["value"]))
                    }), 128))
                  ]),
                  _: 1
                }, 8, ["value"])
              ]),
              _createElementVNode("div", _hoisted_10, [
                _createVNode(_component_es_button, {
                  label: _unref(t)('action.close'),
                  onClick: closePermissionsModal,
                  "is-primary": false
                }, null, 8, ["label"])
              ])
            ]))
          : _createCommentVNode("", true)
      ]),
      _: 1
    }, 8, ["visible"])
  ]))
}
}

})